import { useMemo } from "react";
import { AgendaList } from "@/components/views/calendar/AgendaList";
import { EarningsArticlesTable } from "@/components/views/calendar/EarningsArticlesTable";
import { EarningsDetailPanel } from "@/components/views/calendar/EarningsDetailPanel";
import { MonthGrid } from "@/components/views/calendar/MonthGrid";
import { useEarningsByMonth, useUpcomingEarnings } from "@/hooks/api";
import type { EarningsCalendarItemOut } from "@/api/client";

export interface CalendarMonth {
  year: number;
  month: number;
}

export function CalendarMain({
  selectedId,
  onSelect,
  calendarMonth,
  setCalendarMonth,
}: {
  selectedId: number | null;
  onSelect: (item: EarningsCalendarItemOut) => void;
  calendarMonth: CalendarMonth;
  setCalendarMonth: (m: CalendarMonth) => void;
}) {
  const { data: monthEvents } = useEarningsByMonth(calendarMonth.year, calendarMonth.month);
  const { data: upcomingEvents } = useUpcomingEarnings(100);

  const selectedEvent = useMemo<EarningsCalendarItemOut | null>(() => {
    if (selectedId === null) return null;
    return (
      (monthEvents ?? []).find((e) => e.id === selectedId) ??
      (upcomingEvents ?? []).find((e) => e.id === selectedId) ??
      null
    );
  }, [selectedId, monthEvents, upcomingEvents]);

  function shiftMonth(delta: number) {
    const next = new Date(calendarMonth.year, calendarMonth.month - 1 + delta, 1);
    setCalendarMonth({ year: next.getFullYear(), month: next.getMonth() + 1 });
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
      <MonthGrid
        year={calendarMonth.year}
        month={calendarMonth.month}
        events={monthEvents ?? []}
        selectedId={selectedId}
        onPrev={() => shiftMonth(-1)}
        onNext={() => shiftMonth(1)}
        onSelectEvent={onSelect}
        className="hidden md:flex"
      />
      <div className="md:hidden">
        <AgendaList
          year={calendarMonth.year}
          month={calendarMonth.month}
          events={monthEvents ?? []}
          upcomingEvents={upcomingEvents ?? []}
          selectedId={selectedId}
          onPrev={() => shiftMonth(-1)}
          onNext={() => shiftMonth(1)}
          onSelectEvent={onSelect}
        />
      </div>
      {selectedEvent && <EarningsDetailPanel event={selectedEvent} />}
      {selectedEvent && selectedEvent.primary_symbol && (
        <EarningsArticlesTable
          symbol={selectedEvent.primary_symbol}
          anchorDate={selectedEvent.announcement_date}
        />
      )}
    </div>
  );
}
